import { Box, Typography, Menu, MenuItem, Card, FormControlLabel, FormGroup, Checkbox, IconButton, SvgIcon, Tooltip, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";

// icons
import SlideshowIcon from "@mui/icons-material/Slideshow";
import ImageIcon from "@mui/icons-material/Image";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DotsVerticalIcon from "@untitled-ui/icons-react/build/esm/DotsVertical";
import SyncIcon from "@mui/icons-material/Sync";
import toast from "react-hot-toast";
import { FaTwitch, FaYoutube } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { editWidget, pullContentFromFolder } from "src/redux/actions/Content";
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { getNiceTimestamp } from "src/utils/get-nice-timestamp";
import { api } from "src/api/endpoints";
import { formatWebsiteUrl } from "../Widgets/DialogBoxWebSite";
import { isLiveWidget } from "src/utils/liveWidgets";

const PMW_PROCESSING_MS = 2 * 60 * 1000;

const normalizeYoutubeStatus = (ytStatus) => {
  if (Array.isArray(ytStatus)) {
    if (ytStatus.includes('error')) {
      return 'error';
    }

    if (ytStatus.includes('pending')) {
      return 'pending';
    }

    return 'success';
  }

  return ytStatus || 'success';
};

  function detectUrlType(u) {
    if (!u || typeof u !== 'string') return 'unknown';
    const lower = u.split('?')[0].split('#')[0].toLowerCase();

    if (lower.match(/\.(mp4|webm|mov|m4v)$/)) return 'video';
    if (lower.match(/\.(jpe?g|png|gif|webp)$/)) return 'image';
    return 'unknown';
  }

function ListViewFile({
  setRefreshData,
  url,
  type,
  name,
  createdAt,
  onAddPlaylist,
  onPushScreen,
  onMove,
  onDelete,
  contentObject,
  onCheckedSingleContent,
  onUncheckedSingleContent,
  onPreview,
  handleSeeSplitScreen,
  handleTwitchPreview,
  handleSeeTwitchDialog,
  handleSeeYoutubeDialog,
  folders,
  handleOpenFoldersDataDialog,
  checkFilesPlaylists,
  onEditWidget,
  setCurrentContentPreview
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openSplitScreenDialog, setOpenSplitScreenDialog] = React.useState(false);
  const [savedZones, setSavedZones] = React.useState({});

  const [fileMessage, setFileMessage] = useState('');
  const [isResyncing, setIsResyncing] = useState(false);

  const dispatch = useDispatch();

  const handleResyncPMW = async () => {
    try {
      const credsRaw = window.localStorage.getItem("PMWcred");
      const creds = credsRaw ? JSON.parse(credsRaw) : {};
      const hasValidCreds = Boolean(creds?.access_token && creds?.user_id && creds?.expires_at > Date.now());

      if (!hasValidCreds) {
        toast.error("PosterMyWall authorization missing or expired");
        return;
      }

      setIsResyncing(true);

      const { data: designsData } = await api.get(`/postermywall/users/${creds.user_id}/designs`, {
        headers: {
          Authorization: `Bearer ${creds.access_token}`,
        },
      });

      const list = Array.isArray(designsData) ? designsData : [];
      if (list.length === 0) {
        toast.error("No PosterMyWall designs found");
        return;
      }

      const widgetName = (name || "").trim().toLowerCase();
      const matchedDesign =
        list.find((d) => (d?.name || "").trim().toLowerCase() === widgetName) ||
        list.find((d) => (d?.name || "").toLowerCase().includes(widgetName)) ||
        list[0];

      if (!matchedDesign?.id) {
        toast.error("No matching PosterMyWall design found");
        return;
      }

      const { data: downloadData, status } = await api.get(
        `/postermywall/users/${creds.user_id}/designs/${matchedDesign.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${creds.access_token}`,
          },
        }
      );

      if (status !== 200 || !downloadData?.download_url) {
        throw new Error("PosterMyWall did not return a download URL");
      }

      const formattedUrl = formatWebsiteUrl(downloadData.download_url);
      await dispatch(
        editWidget({
          type: "pmw",
          id: contentObject.id,
          data: {
            url: formattedUrl,
          },
        })
      );

      try {
        const mediaCheck = await api.post('/content/check-media-available', {
          url: formattedUrl,
        });

        if (mediaCheck?.data?.isAvailable === false) {
          setFileMessage("Design hasn't been processed yet...");
          window.setTimeout(() => {
            setFileMessage("");
          }, 4000);
          toast.success("PosterMyWall design re-synced, but media is still processing");
        } else {
          toast.success("PosterMyWall design re-synced successfully");
        }
      } catch (checkErr) {
        toast.success("PosterMyWall design re-synced successfully");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to re-sync PosterMyWall design");
    } finally {
      setIsResyncing(false);
    }
  };

  // console.log("CONTENT OBJECT: ", contentObject);

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (type, obj) => {
    if (type === "add_playlist") {
      onAddPlaylist(obj);
    }
    if (type === "push_screen") {
      onPushScreen(obj);
    }
    if (type === "on_move") {
      onMove(obj.id);
    }
    if (type === "on_preview") {
      console.log("OBJ Preview: ", obj?.type);
      if (obj?.type && obj?.type === "split_screen") {
        handleSeeSplitScreen(contentObject);
        // setOpenSplitScreenDialog(true)
        return;
      }
      if (obj?.widget?.widgetCat === "twitch") {
        handleTwitchPreview(contentObject);
        return;
      }
      if (obj?.widget?.widgetCat === "yt_playlist") {
        window.open(obj.widget.url, "_blank");
        return;
      } else {
        onPreview({ url: obj.url, contentType: obj.type, embed: obj.embed, widget: obj.widget });
      }
    }
    if (type === "on_delete") {
      onDelete(obj.id);
    }
    if(type == 'on_edit'){
      if(obj?.widget?.widgetCat === "twitch"){
        handleSeeTwitchDialog(obj)
      }else if(obj?.widget?.widgetCat === "yt_playlist"){
        handleSeeYoutubeDialog(obj)
      }else{
        onEditWidget(obj)
      }
    }
    setAnchorEl(null);
  };
  function timeSince(date) {
    var seconds = Math.floor((new Date(Date.now()) - date) / 1000);

    var interval = seconds / 31536000;

    if (interval > 1) {
      return Math.floor(interval) + "y";
    }
    interval = seconds / 2592000;
    if (interval > 1) {
      return Math.floor(interval) + "mon";
    }
    interval = seconds / 86400;
    if (interval > 1) {
      return Math.floor(interval) + "d";
    }
    interval = seconds / 3600;
    if (interval > 1) {
      return Math.floor(interval) + "h";
    }
    interval = seconds / 60;
    if (interval > 1) {
      return Math.floor(interval) + "min";
    }
    return Math.floor(seconds) + "sec";
  }
  const onChangeSingleContent = (e) => {
    const { checked, value } = e.target;
    if (checked) {
      onCheckedSingleContent(value);
    } else {
      onUncheckedSingleContent(value);
    }
  };

  
    const { currentFolder } = useSelector(
        (state) => ({
          currentFolder: state.FileFolder.currentFolder,
        }),
    );
  
  const handlePullFromFolder = (id) => {
    if(currentFolder !== 'root'){
      dispatch(pullContentFromFolder(id, currentFolder))
    }
  }

  const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
  const [currentTime, setCurrentTime] = React.useState(() => Date.now());

  const handlePMWclick = async (filename, PMWurl) => {
    if (detectUrlType(PMWurl) == 'unknown') {
        return window.open(PMWurl, "_blank");
    }
    try {
        const result = await api.post('/content/check-media-available', {
            url: PMWurl
        })
        const isAvailable = result.data.isAvailable
        if (!isAvailable) {
            setFileMessage(`Design hasn't been processed yet...`)
            const timeoutId = window.setTimeout(() => {
                setFileMessage("");
            }, 4000);
    
            return () => window.clearTimeout(timeoutId);        
        }else {
            handleClose('on_preview', {
                url: PMWurl,
                type: detectUrlType(PMWurl),
                embed: PMWurl,
                widget: null
            })
            // onPreview({ url: obj.url, contentType: obj.type, embed: obj.embed, widget: obj.widget });
        }
    } catch (err) {}
    // (await fetch(url)).text()
  }

  const isYoutubeVideo = contentObject?.widget?.widgetCat === 'youtube';
  const isYoutubePlaylist = contentObject?.widget?.widgetCat === 'yt_playlist';
  const isPostermywallWidget = contentObject?.widget?.widgetCat === 'postermywall';
  const pmwReferenceTime = contentObject?.updatedAt || createdAt;
  const createdAtMs = new Date(pmwReferenceTime).getTime();
  const remainingPmwProcessingMs = isPostermywallWidget && Number.isFinite(createdAtMs)
    ? createdAtMs + PMW_PROCESSING_MS - currentTime
    : 0;
  const showPostermywallSpinner = remainingPmwProcessingMs > 0;

  React.useEffect(() => {
    if (!isPostermywallWidget || !Number.isFinite(createdAtMs)) {
      return;
    }

    const remainingMs = createdAtMs + PMW_PROCESSING_MS - Date.now();
    if (remainingMs <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCurrentTime(Date.now());
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [isPostermywallWidget, createdAtMs]);

  const youtubeStatus = normalizeYoutubeStatus(contentObject?.ytStatus);
  const ytPlaylistStatuses = Array.isArray(contentObject?.ytStatus) ? contentObject.ytStatus : [];
  const ytPlaylistProcessedCount = ytPlaylistStatuses.filter((status) => status !== 'pending').length;
  const ytPlaylistProgress = isYoutubePlaylist && ytPlaylistStatuses.length > 0 && ytPlaylistProcessedCount < ytPlaylistStatuses.length
    ? `${ytPlaylistProcessedCount}/${ytPlaylistStatuses.length}`
    : '';
  const openYoutubePreview = () => {
    const videoId = contentObject?.widget?.url?.match(/(?:youtu\.be\/|\/(?:embed|v)\/|watch\?v=|[?&]v=)([A-Za-z0-9_-]{11})/)?.[1];
    if (videoId) {
      window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank', 'noopener,noreferrer');
    }
  };

  const youtubeTooltipTitle =
    youtubeStatus === 'pending'
      ? `${isYoutubePlaylist ? 'YouTube playlist' : 'YouTube video'} is being processed${ytPlaylistProgress ? ` (${ytPlaylistProgress})` : ''}`
      : youtubeStatus === 'error'
        ? `${isYoutubePlaylist ? 'YouTube playlist' : 'YouTube video'} failed to process${ytPlaylistProgress ? ` (${ytPlaylistProgress})` : ''}`
        : '';

  const youtubeVideoIcon = (
    <Box
      onClick={openYoutubePreview}
      sx={{ position: 'relative', height: '100%', width: '100%', cursor: 'pointer', flexShrink: 0, maxWidth: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <img
        src="/images/youtubeIcon.png"
        alt="YouTube"
        style={{
          width: 50,
          height: 50,
          objectFit: 'contain',
          filter: youtubeStatus === 'error' ? 'grayscale(1) contrast(0.95)' : 'none',
        }}
      />

      {youtubeStatus === 'pending' ? (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            borderRadius: '50%',
            width: 24,
            height: 24,
          }}>
          <CircularProgress size={18} thickness={6} />
        </Box>
      ) : null}

      {youtubeStatus === 'error' ? (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            borderRadius: '50%',
            width: 24,
            height: 24,
          }}>
          <CloseRoundedIcon sx={{ color: '#d32f2f', fontSize: 21 }} />
        </Box>
      ) : null}

      {contentObject?.widget?.widgetCat === 'yt_playlist' && ytPlaylistProgress ? (
        <Box
          sx={{
            position: 'absolute',
            right: -8,
            bottom: -2,
            minWidth: 28,
            height: 18,
            px: 0.5,
            borderRadius: '999px',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 1,
          }}>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1, fontWeight: 700 }}>
            {ytPlaylistProgress}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );

  const youtubePlaylistIcon = (
    <Box
      onClick={openYoutubePreview}
      sx={{ position: 'relative', height: '100%', width: '100%', cursor: 'pointer', flexShrink: 0, maxWidth: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <img
        src="/images/yt_app.png"
        alt="YouTube playlist"
        style={{
          width: 60,
          height: 60,
          objectFit: 'contain',
          filter: youtubeStatus === 'error' ? 'grayscale(1) contrast(0.95)' : 'none',
        }}
      />

      {ytPlaylistStatuses.length > 0 && youtubeStatus === 'pending' ? (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            borderRadius: '50%',
            width: 24,
            height: 24,
          }}>
          <CircularProgress size={18} thickness={6} />
        </Box>
      ) : null}

      {ytPlaylistStatuses.length > 0 && youtubeStatus === 'error' ? (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            borderRadius: '50%',
            width: 24,
            height: 24,
          }}>
          <CloseRoundedIcon sx={{ color: '#d32f2f', fontSize: 21 }} />
        </Box>
      ) : null}

      {ytPlaylistProgress ? (
        <Box
          sx={{
            position: 'absolute',
            right: -8,
            bottom: -2,
            minWidth: 28,
            height: 18,
            px: 0.5,
            borderRadius: '999px',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 1,
          }}>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1, fontWeight: 700 }}>
            {ytPlaylistProgress}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <>
      <Card
        sx={{
          backgroundColor: "transparent",
          boxShadow: 0,
          height: "60px",
          width: "100%",
          margin: "5px auto",
          display: "flex",
          transition: (theme) =>
            theme.transitions.create(["background-color, box-shadow"], {
              easing: theme.transitions.easing.easeInOut,
              duration: 200,
            }),
          "&:hover": {
            backgroundColor: "background.paper",
            boxShadow: 16,
          },
        }}
        variant="outlined">
        <Box
          sx={{
            width: isMobile ? "70%" : "50%",
            height: "100%",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            paddingLeft: "20px",
          }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={onChangeSingleContent}
                  checked={contentObject?.checked ? true : false}
                  value={contentObject.id}
                  disabled={isPostermywallWidget && showPostermywallSpinner}
                />
              }
              label=""
            />
          </FormGroup>

          {type === "split_screen" ? (
            <Box
              sx={{ height: "80%", width: "100px", cursor: "pointer", flexShrink: 0, maxWidth: "60px" }}
              onClick={() => {
                handleSeeSplitScreen(contentObject);
                setOpenSplitScreenDialog(true);
              }}>
              <a style={{ color: "white", cursor: "pointer", textDecoration: "none" }}>
                <img src={contentObject.widget?.widgetIcon} style={{ maxWidth: "80%", maxHeight: "100%" }} />
              </a>
            </Box>
          ) : contentObject?.widget?.widgetCat === "website" || contentObject?.widget?.widgetCat === "postermywall" ? (
            <Box sx={{ height: "80%", width: "100px", cursor: "pointer", flexShrink: 0, maxWidth: "60px", position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <a role="button" style={{ color: "white", cursor: "pointer", textDecoration: "none" }} onClick={() => handlePMWclick(name, contentObject.widget.url)}>
                <img
                  src={contentObject.widget?.widgetIcon}
                  style={{
                    maxWidth: "80%",
                    maxHeight: "100%",
                    opacity: isPostermywallWidget && showPostermywallSpinner ? 0.45 : 1,
                  }}
                />
              </a>
              {isPostermywallWidget && showPostermywallSpinner ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    // left: "50%",
                    // top: "50%",
                    transform: "translate(-4px, -3px)",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}>
                  <CircularProgress size={20} thickness={6} sx={{ color: '#fff' }} />
                </Box>
              ) : null}
            </Box>
          ) : isYoutubeVideo ? (
            youtubeStatus === 'pending' || youtubeStatus === 'error' ? (
              <Tooltip title={youtubeTooltipTitle} placement="top">
                {youtubeVideoIcon}
              </Tooltip>
            ) : (
              youtubeVideoIcon
            )
          ) : isYoutubePlaylist ? (
            ytPlaylistStatuses.length > 0 && (youtubeStatus === 'pending' || youtubeStatus === 'error') ? (
              <Tooltip title={youtubeTooltipTitle} placement="top">
                {youtubePlaylistIcon}
              </Tooltip>
            ) : (
              youtubePlaylistIcon
            )
          ) : contentObject?.widget?.widgetCat === "twitch" ? (
            // /twitchtv?chat=true&channel=lablabai
            <Box onClick={() => handleTwitchPreview(contentObject)} sx={{ height: "80%", width: "100px", cursor: "pointer", flexShrink: 0, maxWidth: "60px" }}>
              <FaTwitch size={"32px"} style={{ marginTop: "10px", color: "#9147ff" }} />
            </Box>
          ) : (
            <Box sx={{ height: "80%", width: "100px", cursor: "pointer", flexShrink: 0, maxWidth: "60px" }} onClick={() => setCurrentContentPreview(contentObject)}>
              {type === "image" && <img src={url} style={{ height: "100%", width: "100%", objectFit: "contain" }} />}
              {type === "video" && <video src={url} style={{ height: "100%", width: "100%", objectFit: "cover" }}></video>}
              {contentObject.embed === "embed" && <embed src={contentObject.url} style={{ height: "100%", width: "100%", objectFit: "cover" }}></embed>}
              {type === "widget" && <img src={contentObject.widget?.widgetIcon} style={{ maxWidth: "80%", maxHeight: "100%" }} />}
            </Box>
          )}
          {}

          <Box sx={{ height: "4vh", width: "2vw", display: {xs: 'none', md: 'flex'}, justifyContent: "center", alignItems: "center" }}>
            {type === "image" && <ImageIcon htmlColor="#6c737f" />}
            {type === "video" && <SlideshowIcon htmlColor="#6c737f" />}
            {type === "widget" && <AppRegistrationIcon htmlColor="#6c737f" />}
            {type === "split_screen" && <AppRegistrationIcon htmlColor="#6c737f" />}
          </Box>
          <Box>
            <Tooltip title={name.length > 30 ? name : ''} placement="right">
                {fileMessage ? (
                    <Typography variant="subtitle2" color={"warning.main"}>{fileMessage}</Typography>
                ) : (
                    <Typography variant="subtitle2">{name.length > 30 ? `${name.slice(0, 30)}...` : name}</Typography>
                )}
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            width: isMobile ? "30%" : "50%",
            height: "100%",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "10px",
            paddingRight: "20px",
          }}>

          {isPostermywallWidget ? (
            <Tooltip title="Click this button to apply changes made on postermywall.com.">
              <span>
                <IconButton onClick={handleResyncPMW} disabled={isResyncing || showPostermywallSpinner}>
                  {isResyncing ? <CircularProgress size={20} /> : <SyncIcon />}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}

          {/* {folders.length > 0 ? (
            <Box>
              <Tooltip title={folders.map((fl, index) => `${index !== 0 ? ' ,' : ''}${fl.name}`)}>
                <FolderOpenOutlinedIcon/>
              </Tooltip>
              <Typography color="text.secondary" variant="caption" sx={{ display: { md: "block", xs: "none" } }}>
                {folders.slice(0, 2).map((fl, index) => {
                  return `${index !== 0 ? ' ,' : ''}${fl.name.slice(0, 20)}${fl.name.length > 20 ? '...' : ''}`
                })}
                {folders.length > 2 ? '...' : ''}
              </Typography>
            </Box>
          ) : null} */}
          {folders.length > 0 ? (
            <IconButton onClick={() => handleOpenFoldersDataDialog(folders)}>
              <FolderOpenOutlinedIcon/>
            </IconButton>
          ) : null}

          <IconButton onClick={() => checkFilesPlaylists(contentObject.id)} sx={{display: {xs: 'none', md: 'block'}}}>
            <SubscriptionsIcon />
          </IconButton>

          <Typography color="text.secondary" variant="caption" sx={{ display: { md: "block", xs: "none" } }} width={180} textAlign="end">
            {getNiceTimestamp(new Date(createdAt), true)}
          </Typography>

          {/* {type !== "split_screen" &&  */}
          <IconButton
            onClick={handleClick}
            aria-controls={open ? "content-menu" : undefined}
            aria-haspopup={"true"}
            aria-expanded={open ? "true" : undefined}
            id="content-menu-btn">
            <SvgIcon fontSize="small">
              <DotsVerticalIcon />
            </SvgIcon>
          </IconButton>
          {/* } */}
          <Menu
            id={"content-menu"}
            anchorEl={anchorEl}
            open={open}
            MenuListProps={{
              "aria-labelledby": "content-menu-btn",
            }}
            onClose={handleClose}
            keepMounted
            anchorOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}>
            {type !== "split_screen" && !isLiveWidget(contentObject) && <MenuItem onClick={() => handleClose("add_playlist", contentObject)}>{t("views.listViewFile.addToPlaylist")}</MenuItem>}
            {/* <MenuItem onClick={() => handleClose("push_screen", contentObject)}>{t("views.listViewFile.pushToScreen")}</MenuItem> */}
            {currentFolder == 'root' ? (
              <MenuItem onClick={() => handleClose("on_move", contentObject)}>{t("views.listViewFile.move")}</MenuItem>
            ) : (
              <MenuItem onClick={() => handlePullFromFolder(contentObject.id)}>{t("views.listViewFile.pullFromFolder")}</MenuItem>
            )}
            {contentObject?.widget?.widgetCat === "website" || contentObject?.widget?.widgetCat === "postermywall" ? (
              <MenuItem>
                <a style={{ color: "white", cursor: "pointer", textDecoration: "none" }} href={contentObject?.widget?.url} target="_blank">
                  {t("views.listViewFile.preview")}
                </a>
              </MenuItem>
            ) : (
              <MenuItem onClick={() => handleClose("on_preview", contentObject)}>{t("views.listViewFile.preview")}</MenuItem>
            )}
            {contentObject?.widget?.widgetCat === 'youtube' || isLiveWidget(contentObject) || contentObject?.widget?.widgetCat === "twitch" || contentObject?.widget?.widgetCat === "yt_playlist" || contentObject?.widget?.widgetCat === "weather" || contentObject?.widget?.widgetCat === "vimeo" || contentObject?.widget?.widgetCat === "website" || contentObject?.widget?.widgetCat === "embed" || (contentObject?.widget?.widgetCat === "scrollingstrip" && contentObject?.widget?.scrollingstrip) || contentObject?.widget?.widgetCat === "canva" || contentObject?.widget?.widgetCat === "postermywall" ? (
              <MenuItem onClick={() => handleClose('on_edit', contentObject)}>{t("views.listViewFile.edit")}</MenuItem>
            ) : null}
            <MenuItem onClick={() => handleClose("on_delete", contentObject)}>{t("views.listViewFile.delete")}</MenuItem>
          </Menu>
        </Box>
      </Card>
    </>
  );
}

export default ListViewFile;
